import { _LTracker } from '@ignite/loggly-jslogger';
import Raven from 'raven-js';

import ILogglyData from '../interfaces/ILogglyData';
import ILogglyPayload from '../interfaces/ILogglyPayload';
import INetworkErrorLog from '../interfaces/INetworkErrorLog';

const tags: { [key: string]: string } = {
  'app-key': 'hx-connect-chat-ui',
  'aws-account-key': process.env.REACT_APP_AWS_ACCOUNT_KEY || '',
  'node-env': process.env.NODE_ENV || '',
  'release': process.env.REACT_APP_VERSION || '',
};

const ravenTags = {
  ...tags,
  severity: 'error',
};

const convertTagsToString = (tagsInput: object) => {
  const tagsArray: string[] = [];

  for (const key in tagsInput) {
    if (tagsInput.hasOwnProperty(key)) {
      tagsArray.push(`${key}.${tags[key]}`);
    }
  }

  return tagsArray.join(',');
};

export function init(appInitializer: () => void) {
  // Initialize Loggly logging
  _LTracker.push({
    logglyKey: '58313b69-1b94-4376-a912-7f6ae32f3705',
    sendConsoleErrors: true,
    tag: convertTagsToString(tags),
  });

  // Initialize Sentry logging
  Raven.config(process.env.REACT_APP_SENTRY_URI || '', {
    environment: process.env.REACT_APP_AWS_ACCOUNT_KEY,
    release: process.env.REACT_APP_VERSION,
    tags: ravenTags,
  }).install();
  Raven.context(appInitializer);
}

type LogType = 'alert' | 'critical' | 'debug' | 'emergency' | 'error' | 'info' | 'notice' | 'warning';

function buildLogglyPayload(logTypeTag: LogType, message: string, data?: ILogglyData) {
  const payload: ILogglyPayload = { message };

  if (data) {
    payload.cid = data.correlationId;
    payload.ctxt = data.context;

    if (data.tags) {
      payload.tag = [...data.tags, logTypeTag].join(',');
    }
  }

  if (!payload.tag) {
    payload.tag = logTypeTag;
  }

  return payload;
}

function buildNetworkErrorMessage(message: string, url: string = 'unknown') {
  return `Request to "${url}" failed with error "${message}"`;
}

export const testing = {
  buildLogglyPayload,
  buildNetworkErrorMessage,
};

export function log(message: string, data?: ILogglyData) {
  if (process.env.NODE_ENV === 'development') {
    // tslint:disable-next-line: no-console
    console.log('LOG', message, data);
  } else {
    const payload = buildLogglyPayload('info', message, data);
    _LTracker.push(payload);
  }
}

export function logAlert(message: string, data?: ILogglyData) {
  if (process.env.NODE_ENV === 'development') {
    // tslint:disable-next-line: no-console
    console.log('ALERT', message, data);
  } else {
    const payload = buildLogglyPayload('alert', message, data);
    _LTracker.push(payload);
  }
}

export function logCritical(message: string, data?: ILogglyData) {
  if (process.env.NODE_ENV === 'development') {
    // tslint:disable-next-line: no-console
    console.log('CRITICAL', message, data);
  } else {
    const payload = buildLogglyPayload('critical', message, data);
    _LTracker.push(payload);
  }
}

export function logDebug(message: string, data?: ILogglyData) {
  if (process.env.NODE_ENV === 'development') {
    // Fixes IE 10 issue where console.debug is not defined
    // tslint:disable-next-line: no-console
    if (typeof console.debug !== 'undefined') {
      // tslint:disable-next-line: no-console
      console.debug('DEBUG', message, data);
    }
  } else {
    const payload = buildLogglyPayload('debug', message, data);
    _LTracker.push(payload);
  }
}

export function logEmergency(message: string, data?: ILogglyData) {
  if (process.env.NODE_ENV === 'development') {
    // tslint:disable-next-line: no-console
    console.log('EMERGENCY', message, data);
  } else {
    const payload = buildLogglyPayload('emergency', message, data);
    _LTracker.push(payload);
  }
}

export function logError(message: string, data?: ILogglyData) {
  if (process.env.NODE_ENV === 'development') {
    // tslint:disable-next-line: no-console
    console.error('ERROR', message, data);
  } else {
    const payload = buildLogglyPayload('error', message, data);
    _LTracker.push(payload);
  }
}

export function logException(error: Error, data?: ILogglyData) {
  if (process.env.NODE_ENV === 'development') {
    // tslint:disable-next-line: no-console
    console.error('EXCEPTION', error, data);
  } else {
    const payload = buildLogglyPayload('error', error.toString(), data);
    _LTracker.push(payload);
  }
}

export function logInfo(message: string, data?: ILogglyData) {
  if (process.env.NODE_ENV === 'development') {
    // tslint:disable-next-line: no-console
    console.info('INFO', message, data);
  } else {
    const payload = buildLogglyPayload('info', message, data);
    _LTracker.push(payload);
  }
}

export function logNetworkError(data: INetworkErrorLog) {
  if (process.env.NODE_ENV === 'development') {
    // tslint:disable-next-line: no-console
    console.error('NETWORK ERROR', data);
  } else {
    const message = buildNetworkErrorMessage(data.error.message, data.url);
    const payload = buildLogglyPayload('error', message);
    _LTracker.push(payload);
  }
}

export function logNotice(message: string, data?: ILogglyData) {
  if (process.env.NODE_ENV === 'development') {
    // tslint:disable-next-line: no-console
    console.info('NOTICE', message, data);
  } else {
    const payload = buildLogglyPayload('notice', message, data);
    _LTracker.push(payload);
  }
}

export function logWarning(message: string, data?: ILogglyData) {
  if (process.env.NODE_ENV === 'development') {
    // tslint:disable-next-line: no-console
    console.warn(message, data);
  } else {
    const payload = buildLogglyPayload('warning', message, data);
    _LTracker.push(payload);
  }
}
